import React, { useEffect, useState } from 'react';
import { authFetch, API } from '../api';
import { presentPdf } from '../labelPrint';

// Settings → Badges & rights (guided P&P phase 1, 2026-10-17; docs/GUIDED-PP.md).
// Fernando: the stations know a person by their BADGE, the tablets by a 4-digit PIN entered every round;
// rights are per person with tiers as presets; admins issue badges and PINs, managers may look.
// Backend: backend/pp_identity.py (/api/pp2/*), backend/permissions.py.

interface Perm { key: string; label: string }
interface Tier { key: string; label: string; perms: string[]; position: number; people: number }
interface Person {
  username: string; role: string; name: string; lang: string;
  tier: string | null; grants: string[]; revokes: string[]; note: string | null; perms: string[];
  badge_hint: string | null; badge_issued_at: string | null; badge_last_used_at: string | null;
  pin_set_at: string | null; pin_locked_at: string | null; pin_failed_count: number | null;
  pin: string | null;          // admins only (Fernando 19.10: "Everybody's PINs are visible for Admins")
}
interface Device {
  username: string; role: string; is_device: boolean; user_active: boolean;
  kind: 'station' | 'tablet' | 'inbound' | null; label: string | null; station_no: number | null; cart_id: string | null;
  registered: boolean; session_person: string | null;
}
interface Cart { id: string; name: string; boxes: number }

const card = 'bg-[#1a1d27] rounded-xl border border-gray-800 p-5';
const head = 'text-gray-400 text-xs uppercase tracking-widest font-semibold mb-3';
const btn = 'px-2.5 py-1 rounded-md text-xs font-semibold bg-gray-700 hover:bg-gray-600 text-white disabled:opacity-40';
const btnBlue = 'px-2.5 py-1 rounded-md text-xs font-semibold bg-blue-600 hover:bg-blue-500 text-white disabled:opacity-40';
const input = 'px-2 py-1 rounded bg-[#0f1117] border border-gray-700 text-white text-sm';
const fmt = (s: string | null) => (s ? new Date(s + (s.endsWith('Z') ? '' : 'Z')).toLocaleDateString() : '');

async function errText(r: Response) {
  const d = await r.json().catch(() => ({} as any));
  return d.detail || `HTTP ${r.status}`;
}

export default function BadgesRightsSettings() {
  const [perms, setPerms] = useState<Perm[]>([]);
  const [tiers, setTiers] = useState<Tier[]>([]);
  const [rolePerms, setRolePerms] = useState<Record<string, string[]>>({});
  const [people, setPeople] = useState<Person[]>([]);
  const [canEdit, setCanEdit] = useState(false);
  const [devices, setDevices] = useState<Device[]>([]);
  const [carts, setCarts] = useState<Cart[]>([]);
  const [open, setOpen] = useState<string | null>(null);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  const load = async () => {
    try {
      const [c, p, d] = await Promise.all([
        authFetch(`${API}/pp2/catalogue`).then(r => r.json()),
        authFetch(`${API}/pp2/people`).then(async r => { if (!r.ok) throw new Error(await errText(r)); return r.json(); }),
        authFetch(`${API}/pp2/devices`).then(r => r.ok ? r.json() : { devices: [], carts: [] }),
      ]);
      setPerms(c.perms || []); setTiers(c.tiers || []); setRolePerms(c.role_perms || {});
      setPeople(p.people || []); setCanEdit(!!p.can_edit);
      setDevices(d.devices || []); setCarts(d.carts || []);
      setErr('');
    } catch (e: any) { setErr(e.message || 'Could not load'); }
  };
  useEffect(() => { load(); }, []);

  const permLabel = (k: string) => perms.find(p => p.key === k)?.label || k;

  const issueBadge = async (p: Person) => {
    if (p.badge_hint && !window.confirm(`Issue a new badge for ${p.name}? Their current badge (…${p.badge_hint}) stops working at once.`)) return;
    const win = window.open('', '_blank', 'width=520,height=420');
    const r = await authFetch(`${API}/pp2/people/${encodeURIComponent(p.username)}/badge`, { method: 'POST' });
    if (!r.ok) { if (win) win.close(); setErr(await errText(r)); return; }
    const blob = await r.blob();
    presentPdf(win, blob, { title: `Badge — ${p.name}`, filename: `badge-${p.username}.pdf`, autoPrint: true });
    // the badge (27.10): one A4, two identical 9.1 × 6.2 cm sides with the PIN — print at 100 %, cut, fold
    const pinNew = r.headers.get('X-Badge-Pin-New') === '1';
    setMsg(`New badge for ${p.name} (…${r.headers.get('X-Badge-Hint') || ''}) with their PIN on it${pinNew ? ' — they had no PIN, so a new one was made and printed' : ''}. `
      + `Print it now at 100 % ("Actual size"), cut along the outer line and fold on the dashed one — the code is not stored anywhere else; `
      + `if the print fails, issue a new one.`);
    load();
  };
  const revokeBadge = async (p: Person) => {
    if (!window.confirm(`Revoke ${p.name}'s badge (…${p.badge_hint})? It stops working at once.`)) return;
    const r = await authFetch(`${API}/pp2/people/${encodeURIComponent(p.username)}/badge`, {
      method: 'DELETE', body: JSON.stringify({ reason: 'revoked in Settings' }) });
    if (!r.ok) { setErr(await errText(r)); return; }
    setMsg(`${p.name}'s badge is revoked.`); load();
  };
  // New PIN: the WMS picks a 4-digit PIN nobody else has (Fernando 19.10) — the PIN alone says who it is
  const newPin = async (p: Person) => {
    if (p.pin_set_at && !window.confirm(`Give ${p.name} a new PIN? The old one stops working.`)) return;
    const r = await authFetch(`${API}/pp2/people/${encodeURIComponent(p.username)}/pin/new`, { method: 'POST' });
    if (!r.ok) { setErr(await errText(r)); return; }
    const d = await r.json();
    setMsg(`${p.name}'s new PIN: ${d.pin}`); setErr(''); load();
  };
  const unlockPin = async (p: Person) => {
    const r = await authFetch(`${API}/pp2/people/${encodeURIComponent(p.username)}/pin/unlock`, { method: 'POST' });
    if (!r.ok) { setErr(await errText(r)); return; }
    setMsg(`${p.name}'s PIN is unlocked.`); load();
  };
  const removePin = async (p: Person) => {
    if (!window.confirm(`Remove ${p.name}'s PIN? They cannot open a round on a tablet until a new one is set.`)) return;
    const r = await authFetch(`${API}/pp2/people/${encodeURIComponent(p.username)}/pin`, { method: 'DELETE' });
    if (!r.ok) { setErr(await errText(r)); return; }
    setMsg(`${p.name}'s PIN is removed.`); load();
  };
  const saveRights = async (p: Person, tier: string | null, grants: string[], revokes: string[], note: string) => {
    const r = await authFetch(`${API}/pp2/people/${encodeURIComponent(p.username)}/permissions`, {
      method: 'PUT', body: JSON.stringify({ tier, grants, revokes, note }) });
    if (!r.ok) { setErr(await errText(r)); return; }
    setMsg(`Rights saved for ${p.name}.`); setErr(''); setOpen(null); load();
  };

  return (
    <div className="space-y-6">
      <div className={card}>
        <div className={head}>Badges & rights</div>
        <p className="text-sm text-gray-400">
          Every step starts with <b className="text-gray-200">"Identify yourself"</b>: the person's <b className="text-gray-200">PIN</b> (4 digits, on the
          station and the cart's tablet) or <b className="text-gray-200">badge</b> (scanned at the station) — no names to choose, so every PIN is
          different: <b className="text-gray-200">New PIN</b> lets the WMS pick one nobody else has. Admins see everybody's PIN here. Wrong PINs make
          that screen wait (3 in a row: 30 s; 10 in 15 min: 15 min).
          A <b className="text-gray-200">badge</b> prints on one A4: two identical 9.1 × 6.2 cm sides with the name, the barcode and the PIN — cut out,
          fold, done. The PIN is on the card, so a new PIN needs a new badge.
          Rights come from the person's <b className="text-gray-200">tier</b>, plus or minus single rights. Admins have every right, managers every right
          except this page's changes. A staff member with no tier has no rights on the guided screens.
        </p>
        {!canEdit && <p className="text-xs text-amber-300 mt-2">View only — an admin makes the changes.</p>}
        {msg && <div className="text-sm text-green-400 mt-3">{msg}</div>}
        {err && <div className="text-sm text-red-400 mt-3">{err}</div>}
      </div>

      <div className={card}>
        <div className={head}>People</div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-gray-500 border-b border-gray-800">
                <th className="py-2 pr-3">Person</th><th className="py-2 pr-3">Tier · rights</th>
                <th className="py-2 pr-3">Badge</th><th className="py-2 pr-3">PIN</th>
              </tr>
            </thead>
            <tbody>
              {people.map(p => (
                <React.Fragment key={p.username}>
                  <tr className="border-b border-gray-800/60 align-top">
                    <td className="py-2 pr-3">
                      <div className="text-white">{p.name}</div>
                      <div className="text-xs text-gray-500">{p.username} · {p.role}</div>
                    </td>
                    <td className="py-2 pr-3">
                      <div className="text-gray-200">{p.role === 'staff' ? (tiers.find(t => t.key === p.tier)?.label || <span className="text-amber-300">no tier</span>) : `${p.role} (all${p.role === 'manager' ? ' but badges' : ''})`}</div>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {p.perms.map(k => (
                          <span key={k} title={permLabel(k)}
                            className={`px-1.5 py-0.5 rounded text-[11px] ${p.grants.includes(k) ? 'bg-green-900/50 text-green-300' : 'bg-gray-800 text-gray-300'}`}>{k}</span>
                        ))}
                        {p.revokes.map(k => <span key={k} title={`revoked: ${permLabel(k)}`} className="px-1.5 py-0.5 rounded text-[11px] bg-red-900/40 text-red-300 line-through">{k}</span>)}
                      </div>
                      {canEdit && p.role === 'staff' && (
                        <button className={`${btn} mt-1`} onClick={() => setOpen(open === p.username ? null : p.username)}>
                          {open === p.username ? 'Close' : 'Edit rights'}</button>
                      )}
                    </td>
                    <td className="py-2 pr-3 whitespace-nowrap">
                      {p.badge_hint
                        ? <div className="text-gray-200">…{p.badge_hint}<div className="text-xs text-gray-500">issued {fmt(p.badge_issued_at)}{p.badge_last_used_at ? ` · used ${fmt(p.badge_last_used_at)}` : ''}</div></div>
                        : <div className="text-gray-500">none</div>}
                      {canEdit && <div className="flex gap-1 mt-1">
                        <button className={btnBlue} onClick={() => issueBadge(p)}>{p.badge_hint ? 'New badge' : 'Issue badge'}</button>
                        {p.badge_hint && <button className={btn} onClick={() => revokeBadge(p)}>Revoke</button>}
                      </div>}
                    </td>
                    <td className="py-2 pr-3">
                      <PinCell p={p} canEdit={canEdit} onNew={() => newPin(p)} onUnlock={() => unlockPin(p)} onRemove={() => removePin(p)} />
                    </td>
                  </tr>
                  {open === p.username && (
                    <tr className="border-b border-gray-800/60">
                      <td colSpan={4} className="py-3">
                        <RightsEditor p={p} perms={perms} tiers={tiers} rolePerms={rolePerms[p.role] || []}
                          onSave={(t, g, rv, n) => saveRights(p, t, g, rv, n)} />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <TiersCard perms={perms} tiers={tiers} canEdit={canEdit}
        onSaved={m => { setMsg(m); setErr(''); load(); }} onError={setErr} />

      <DevicesCard devices={devices} carts={carts} canEdit={canEdit}
        onSaved={m => { setMsg(m); setErr(''); load(); }} onError={setErr} />
    </div>
  );
}

// What each tier includes — the general setting; a person's own extra / removed rights are set in their row above
// (Fernando 20.10: "Like a general setting and user based. Maybe generally tier 1 can't do anything, but this 1 person
// can. Or generally tier 2 can do these things, but this person is not allowed"). Admins edit; every change is logged.
// 28.10: admins also add a tier (rights copied from one), rename one (pencil on the column) and remove one nobody is on.
function TiersCard({ perms, tiers, canEdit, onSaved, onError }: {
  perms: Perm[]; tiers: Tier[]; canEdit: boolean; onSaved: (m: string) => void; onError: (m: string) => void;
}) {
  const [draft, setDraft] = useState<Record<string, string[]>>({});
  const [renaming, setRenaming] = useState<{ key: string; label: string } | null>(null);
  const [newLabel, setNewLabel] = useState('');
  const [copyFrom, setCopyFrom] = useState('');
  useEffect(() => {
    setDraft(Object.fromEntries(tiers.map(t => [t.key, [...t.perms]])));
    const taken = new Set(tiers.map(t => t.key));
    let n = 1; while (taken.has(`tier${n}`)) n += 1;          // the key the backend will give the next tier
    setNewLabel(`Tier ${n}`);
    setCopyFrom(tiers.length ? tiers[tiers.length - 1].key : '');
  }, [tiers]);
  const rename = async () => {
    if (!renaming) return;
    const t = tiers.find(x => x.key === renaming.key);
    const r = await authFetch(`${API}/pp2/tiers/${encodeURIComponent(renaming.key)}`, {
      method: 'PUT', body: JSON.stringify({ label: renaming.label }) });
    if (!r.ok) { onError(await errText(r)); return; }
    setRenaming(null);
    onSaved(`${t?.label} renamed to ${renaming.label.trim()}.`);
  };
  const remove = async (t: Tier) => {
    if (!window.confirm(`Remove ${t.label}? Nobody is on it.`)) return;
    const r = await authFetch(`${API}/pp2/tiers/${encodeURIComponent(t.key)}`, { method: 'DELETE' });
    if (!r.ok) { onError(await errText(r)); return; }
    onSaved(`${t.label} removed.`);
  };
  const add = async () => {
    const r = await authFetch(`${API}/pp2/tiers`, {
      method: 'POST', body: JSON.stringify({ label: newLabel, copy_from: copyFrom || null }) });
    if (!r.ok) { onError(await errText(r)); return; }
    const d = await r.json();
    const src = tiers.find(x => x.key === copyFrom);
    onSaved(`${d.label} added${src ? ` with ${src.label}'s rights` : ''} — tick its rights below and save.`);
  };
  const toggle = (tk: string, pk: string) => setDraft(d => {
    const cur = d[tk] || [];
    return { ...d, [tk]: cur.includes(pk) ? cur.filter(x => x !== pk) : [...cur, pk] };
  });
  const changed = (t: Tier) => {
    const a = [...(draft[t.key] || [])].sort().join(','), b = [...t.perms].sort().join(',');
    return a !== b;
  };
  const save = async (t: Tier) => {
    const r = await authFetch(`${API}/pp2/tiers/${encodeURIComponent(t.key)}`, {
      method: 'PUT', body: JSON.stringify({ perms: draft[t.key] || [] }) });
    if (!r.ok) { onError(await errText(r)); return; }
    onSaved(`${t.label} saved — everyone on it has these rights within a minute.`);
  };
  return (
    <div className={card}>
      <div className={head}>Tiers — what each tier may do</div>
      <p className="text-sm text-gray-400 mb-3">
        The general setting. A person gets an extra right or loses one in their own row above. Managers and admins have
        every right by their role. On a warehouse computer every change asks for the PIN or badge; these rights decide.
      </p>
      <div className="overflow-x-auto">
        <table className="text-sm">
          <thead>
            <tr className="text-gray-500 text-xs">
              <th className="text-left pr-4 pb-2 font-normal">Right</th>
              {tiers.map(t => (
                <th key={t.key} className="px-3 pb-2 font-semibold text-gray-300 align-top">
                  {renaming?.key === t.key ? (
                    <div className="flex flex-col items-center gap-1">
                      <input autoFocus value={renaming.label} maxLength={40} className={`${input} w-28 text-xs`}
                        onChange={e => setRenaming({ key: t.key, label: e.target.value })}
                        onKeyDown={e => { if (e.key === 'Enter') rename(); if (e.key === 'Escape') setRenaming(null); }} />
                      <div className="flex gap-1">
                        <button className={btnBlue} onClick={rename}>Save</button>
                        <button className={btn} onClick={() => setRenaming(null)}>Cancel</button>
                      </div>
                    </div>
                  ) : (
                    <div>
                      {t.label}
                      {canEdit && <button className="ml-1 text-gray-500 hover:text-gray-200 font-normal" title={`Rename ${t.label}`}
                        onClick={() => setRenaming({ key: t.key, label: t.label })}>✎</button>}
                      <div className="font-normal text-gray-500">
                        {t.people === 1 ? '1 person' : `${t.people} people`}
                        {canEdit && t.people === 0 && tiers.length > 1 && (
                          <button className="ml-1 text-red-400/80 hover:text-red-300" onClick={() => remove(t)}>remove</button>
                        )}
                      </div>
                    </div>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {perms.filter(pm => pm.key !== 'badges_admin').map(pm => (
              <tr key={pm.key} className="border-t border-gray-800/60">
                <td className="pr-4 py-1.5 text-gray-200">{pm.label}</td>
                {tiers.map(t => (
                  <td key={t.key} className="px-3 py-1.5 text-center">
                    <input type="checkbox" disabled={!canEdit} checked={(draft[t.key] || []).includes(pm.key)}
                      onChange={() => toggle(t.key, pm.key)} />
                  </td>
                ))}
              </tr>
            ))}
            {canEdit && (
              <tr>
                <td />
                {tiers.map(t => (
                  <td key={t.key} className="px-3 pt-3 text-center">
                    <button className={btnBlue} disabled={!changed(t)} onClick={() => save(t)}>Save {t.label}</button>
                  </td>
                ))}
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {canEdit && (
        <div className="flex flex-wrap items-center gap-2 mt-4 pt-3 border-t border-gray-800/60 text-sm text-gray-300">
          <span>Add a tier</span>
          <input value={newLabel} maxLength={40} className={`${input} w-36`} onChange={e => setNewLabel(e.target.value)} />
          <span className="text-gray-500">with the rights of</span>
          <select value={copyFrom} onChange={e => setCopyFrom(e.target.value)} className={input}>
            <option value="">— none —</option>
            {tiers.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
          </select>
          <button className={btnBlue} disabled={!newLabel.trim()} onClick={add}>Add</button>
          <span className="text-xs text-gray-500">A tier with people on it cannot be removed — move them first.</span>
        </div>
      )}
    </div>
  );
}

function PinCell({ p, canEdit, onNew, onUnlock, onRemove }: {
  p: Person; canEdit: boolean; onNew: () => void; onUnlock: () => void; onRemove: () => void;
}) {
  const locked = !!p.pin_locked_at;
  return (
    <div>
      {p.pin_set_at
        ? <div className={locked ? 'text-red-400' : 'text-gray-200'}>
            {canEdit && p.pin ? <span className="font-mono text-base tracking-widest">{p.pin}</span> : (locked ? 'Locked' : 'Set')}
            {locked && canEdit && p.pin ? <span className="ml-2">Locked</span> : null}
            {canEdit && !p.pin && <span className="ml-2 text-xs text-amber-300">set before 19.10 — give a new PIN to see it</span>}
            <div className="text-xs text-gray-500">{fmt(p.pin_set_at)}</div></div>
        : <div className="text-gray-500">none</div>}
      {canEdit && <div className="flex gap-1 mt-1 items-center">
        <button className={btnBlue} onClick={onNew}>New PIN</button>
        {locked && <button className={btn} onClick={onUnlock}>Unlock</button>}
        {p.pin_set_at && <button className={btn} onClick={onRemove}>Remove</button>}
      </div>}
    </div>
  );
}

function RightsEditor({ p, perms, tiers, rolePerms, onSave }: {
  p: Person; perms: Perm[]; tiers: Tier[]; rolePerms: string[];
  onSave: (tier: string | null, grants: string[], revokes: string[], note: string) => void;
}) {
  const [tier, setTier] = useState<string>(p.tier || '');
  const [grants, setGrants] = useState<string[]>(p.grants);
  const [revokes, setRevokes] = useState<string[]>(p.revokes);
  const [note, setNote] = useState(p.note || '');
  const fromTier = new Set([...(tiers.find(t => t.key === tier)?.perms || []), ...rolePerms]);
  const state = (k: string) => grants.includes(k) ? 'grant' : revokes.includes(k) ? 'revoke' : 'tier';
  const setState = (k: string, s: string) => {
    setGrants(g => s === 'grant' ? [...g.filter(x => x !== k), k] : g.filter(x => x !== k));
    setRevokes(r => s === 'revoke' ? [...r.filter(x => x !== k), k] : r.filter(x => x !== k));
  };
  return (
    <div className="bg-[#0f1117] rounded-lg border border-gray-800 p-4 space-y-3">
      <label className="text-sm text-gray-300">Tier
        <select value={tier} onChange={e => setTier(e.target.value)} className={`${input} ml-2`}>
          <option value="">— no tier —</option>
          {tiers.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
        </select>
      </label>
      <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1">
        {perms.filter(x => x.key !== 'badges_admin').map(x => {
          const s = state(x.key);
          const has = s === 'grant' || (s === 'tier' && fromTier.has(x.key));
          return (
            <div key={x.key} className="flex items-center gap-2 text-sm">
              <span className={`w-2 h-2 rounded-full ${has ? 'bg-green-400' : 'bg-gray-600'}`} />
              <span className="flex-1 text-gray-300" title={x.key}>{x.label}</span>
              <select value={s} onChange={e => setState(x.key, e.target.value)} className={`${input} text-xs`}>
                <option value="tier">{fromTier.has(x.key) ? 'from tier ✓' : 'from tier —'}</option>
                <option value="grant">grant</option>
                <option value="revoke">revoke</option>
              </select>
            </div>
          );
        })}
      </div>
      <label className="block text-sm text-gray-300">Note
        <input value={note} onChange={e => setNote(e.target.value)} maxLength={300} placeholder="why (optional)"
          className={`${input} w-full mt-1`} />
      </label>
      <button className={btnBlue} onClick={() => onSave(tier || null, grants, revokes, note)}>Save rights</button>
    </div>
  );
}

function DevicesCard({ devices, carts, canEdit, onSaved, onError }: {
  devices: Device[]; carts: Cart[]; canEdit: boolean; onSaved: (m: string) => void; onError: (m: string) => void;
}) {
  const [newLogin, setNewLogin] = useState('');
  const rows = [...devices];
  if (newLogin && !rows.some(d => d.username === newLogin)) {
    rows.push({ username: newLogin, role: 'staff', is_device: false, user_active: true, kind: null, label: null,
                station_no: null, cart_id: null, registered: false, session_person: null });
  }
  return (
    <div className={card}>
      <div className={head}>Devices</div>
      <p className="text-sm text-gray-400 mb-3">
        Each shared login is a <b className="text-gray-200">P&P station</b> (the laptop at a P&P table, with its number), a
        <b className="text-gray-200"> cart tablet</b> (fixed to a cart), an <b className="text-gray-200">inbound station</b> (the
        Inbounds page) or a <b className="text-gray-200">moving station</b> (carried while moving pieces: it opens Tasks and
        does nothing else). A device login is never the person behind what it records — people scan their badge or enter
        their PIN on it — and stays signed in for a week while its screen runs. To add one, create a staff login under Users
        first, then register it here.
      </p>
      <div className="space-y-2">
        {rows.map(d => <DeviceRow key={d.username} d={d} carts={carts} canEdit={canEdit} onSaved={onSaved} onError={onError} />)}
      </div>
      {canEdit && (
        <div className="flex gap-2 items-center mt-3">
          <input value={newLogin} onChange={e => setNewLogin(e.target.value.trim())} placeholder="login name, e.g. Station-1"
            className={`${input} w-56`} />
          <span className="text-xs text-gray-500">then choose its kind below and Save</span>
        </div>
      )}
    </div>
  );
}

function DeviceRow({ d, carts, canEdit, onSaved, onError }: {
  d: Device; carts: Cart[]; canEdit: boolean; onSaved: (m: string) => void; onError: (m: string) => void;
}) {
  const [kind, setKind] = useState<string>(d.kind || '');
  const [stationNo, setStationNo] = useState<string>(d.station_no ? String(d.station_no) : '');
  const [cartId, setCartId] = useState<string>(d.cart_id || '');
  const [label, setLabel] = useState<string>(d.label || '');
  const [active, setActive] = useState<boolean>(d.registered || !d.kind);
  const save = async () => {
    const r = await authFetch(`${API}/pp2/devices/${encodeURIComponent(d.username)}`, {
      method: 'PUT', body: JSON.stringify({ kind, station_no: stationNo ? Number(stationNo) : null, cart_id: cartId || null, label, active }) });
    if (!r.ok) { onError(await errText(r)); return; }
    onSaved(`${d.username} saved as ${kind}${kind !== 'tablet' && stationNo ? ` ${stationNo}` : ''}.`);
  };
  return (
    <div className="flex flex-wrap items-center gap-2 text-sm border-b border-gray-800/60 pb-2">
      <span className="text-white w-32">{d.username}</span>
      <select value={kind} onChange={e => setKind(e.target.value)} disabled={!canEdit} className={input}>
        <option value="">— not registered —</option>
        <option value="station">P&P station</option>
        <option value="tablet">Cart tablet</option>
        <option value="inbound">Inbound station</option>
        <option value="moving">Moving station</option>
      </select>
      {(kind === 'station' || kind === 'inbound' || kind === 'moving') && (
        <label className="text-xs text-gray-400">no.
          <input value={stationNo} onChange={e => setStationNo(e.target.value.replace(/\D/g, '').slice(0, 2))} disabled={!canEdit}
            className={`${input} keep-width w-12 ml-1`} /></label>
      )}
      {kind === 'tablet' && (
        <select value={cartId} onChange={e => setCartId(e.target.value)} disabled={!canEdit} className={input}>
          <option value="">— cart —</option>
          {carts.map(c => <option key={c.id} value={c.id}>{c.name} ({c.boxes} boxes)</option>)}
        </select>
      )}
      <input value={label} onChange={e => setLabel(e.target.value)} disabled={!canEdit} placeholder="label (optional)" className={`${input} w-40`} />
      <label className="text-xs text-gray-400 flex items-center gap-1">
        <input type="checkbox" checked={active} onChange={e => setActive(e.target.checked)} disabled={!canEdit} /> active</label>
      {d.session_person && <span className="text-xs text-green-400">now: {d.session_person}</span>}
      {canEdit && <button className={btnBlue} disabled={!kind} onClick={save}>Save</button>}
    </div>
  );
}
